<template>
  <div 
    class="login-container"
    :class="{ 'opacity-0': !backgroundLoaded }"
    :style="backgroundStyle"
  >
    <div class="login-form-container">
      <div class="max-w-md w-full space-y-6 sm:space-y-8 bg-white/95 dark:bg-gray-800/90 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:shadow-2xl">
        <div>
          <h2 class="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-800 dark:text-white">
            K&C Portfolio Manager
          </h2>
          <p class="mt-2 text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Connexion à votre compte
          </p>
        </div>
        <div v-if="notice" class="bg-green-50 border border-green-400 text-green-800 px-4 py-3 rounded flex items-start justify-between" role="status" aria-live="polite">
          <span class="block sm:inline">{{ notice }}</span>
          <button type="button" class="ml-3 text-green-800 hover:underline focus:outline-none" @click="notice = ''" aria-label="Fermer le message">✕</button>
        </div>
        <form class="mt-6 sm:mt-8 space-y-4 sm:space-y-6" @submit.prevent="handleSubmit">
          <div class="space-y-4">
            <div>
              <label for="email-address" class="sr-only">Adresse email</label>
              <input id="email-address" 
                     name="email" 
                     type="email" 
                     autocomplete="email" 
                     required 
                     v-model="email"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Adresse email">
            </div>
            <div>
              <label for="password" class="sr-only">Mot de passe</label>
              <input id="password" 
                     name="password" 
                     type="password" 
                     autocomplete="current-password" 
                     required 
                     v-model="password"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Mot de passe">
            </div>
          </div>

          <div v-if="error" class="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert" aria-live="assertive">
            <span class="block sm:inline">{{ error }}</span>
          </div>

          <!-- Callout: compte non activé -->
          <div v-if="notActivated" class="bg-yellow-50 border border-yellow-400 text-yellow-800 px-4 py-3 rounded" role="alert" aria-live="polite">
            <p class="mb-2">
              Votre compte n'est pas encore activé. Vous pouvez renvoyer l'email d'activation.
            </p>
            <div class="space-y-2">
              <button type="button" @click="startResend" :disabled="resendLoading || cooldownSeconds > 0"
                      class="w-full py-2 px-4 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                {{ resendLoading ? 'Envoi...' : (cooldownSeconds > 0 ? `Réessayer dans ${cooldownSeconds}s` : "Renvoyer l'email d'activation") }}
              </button>
              <transition name="fade">
                <div v-if="showCaptcha" class="mt-1">
                  <CaptchaWidget v-model="captchaToken" />
                  <button type="button" @click="confirmResend" :disabled="resendLoading"
                          class="mt-2 w-full py-2 px-4 rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50">
                    Confirmer l'envoi
                  </button>
                </div>
              </transition>
              <p v-if="resendMessage" class="text-green-700 text-sm">{{ resendMessage }}</p>
              <p v-if="resendError" class="text-red-700 text-sm">{{ resendError }}</p>
            </div>
            <p class="mt-2 text-xs text-gray-600">
              Vérifiez votre dossier Spams/Promotions.
            </p>
          </div>

          <div>
            <button type="submit" 
                    :disabled="isLoading"
                    class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
              <span v-if="isLoading" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ isLoading ? 'Connexion en cours...' : 'Se connecter' }}
            </button>
          </div>

          <div class="mt-4 space-y-2">
            <!-- Forgot password: primary secondary button -->
            <router-link
              to="/forgot-password"
              class="btn-outline-primary w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0-1.657-1.343-3-3-3S6 9.343 6 11v2m6-2v2m0 0h6m-6 0H6m6-6V5a3 3 0 016 0v2" />
              </svg>
              <span>Mot de passe oublié</span>
            </router-link>

            <!-- Create account: attention-grabbing button -->
            <router-link
              to="/register"
              class="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A9 9 0 1112 21v-2m0 0v-2m0 2h-2m2 0h2" />
              </svg>
              <span>Créer un compte</span>
              <span class="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">Nouveau</span>
            </router-link>

            <!-- Resend activation: lower emphasis link-button -->
            <router-link
              to="/resend-activation"
              class="btn-ghost-indigo w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12H8m0 0l4-4m-4 4l4 4M4 6h16v12H4z" />
              </svg>
              <span>Renvoyer l'activation</span>
            </router-link>
          </div>
        </form>
        <p class="mt-2 text-center text-xs text-gray-500">
          Vérifiez votre dossier Spams/Promotions.
        </p>
      </div>
    </div>
    <AuthFooter @open-legal="isLegalModalOpen = true" />
    <LegalModal :is-open="isLegalModalOpen" @close="isLegalModalOpen = false" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import axiosInstance from '@/config/axios'
import CaptchaWidget from '@/components/common/CaptchaWidget.vue'
import AuthFooter from '@/components/auth/AuthFooter.vue'
import LegalModal from '@/components/auth/LegalModal.vue'

export default {
  name: 'LoginForm',
  components: { CaptchaWidget, AuthFooter, LegalModal },
  setup() {
    const authStore = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    
    const email = ref('')
    const password = ref('')
    const error = ref('')
    const notice = ref('')
    const notActivated = ref(false)
    const showCaptcha = ref(false)
    const captchaToken = ref('')
    const resendLoading = ref(false)
    const resendMessage = ref('')
    const resendError = ref('')
    const cooldownSeconds = ref(0)
    const isLoading = ref(false)
    const isLegalModalOpen = ref(false)
    const backgroundLoaded = ref(false)
    const backgroundStyle = ref({
      backgroundImage: '',
      backgroundColor: '#f3f4f6'
    })

    onMounted(async () => {
      try {
        const { default: bgImage } = await import('@/assets/backgroundImage.js')
        backgroundStyle.value.backgroundImage = `url(${bgImage})`
        backgroundLoaded.value = true
      } catch (err) {
        console.error('Erreur lors du chargement de l\'image:', err)
        backgroundLoaded.value = true
      }

      // Show success message after activation
      if (route.query.activated === '1' || route.query.activated === 1) {
        notice.value = 'Compte activé, vous pouvez vous connecter.'
        setTimeout(() => { if (notice.value) notice.value = '' }, 6000)
      }
    })

    let cooldownTimer
    const startCooldown = (seconds) => {
      cooldownSeconds.value = Math.max(1, Math.floor(seconds))
      clearInterval(cooldownTimer)
      cooldownTimer = setInterval(() => {
        cooldownSeconds.value -= 1
        if (cooldownSeconds.value <= 0) clearInterval(cooldownTimer)
      }, 1000)
    }

    const handleSubmit = async () => {
      try {
        error.value = ''
        notActivated.value = false
        isLoading.value = true
        
        await authStore.login(email.value, password.value)
        
        // Rediriger vers la page demandée ou le dashboard
        const redirectParam = route.query && route.query.redirect
        const redirectPath = (redirectParam ? String(redirectParam) : '') || '/app'
        router.push(redirectPath)
      } catch (err) {
        console.error('Login error:', err)
        // Amélioration de la gestion des erreurs pour afficher le message correct
        if (err.message === 'Réponse invalide du serveur') {
          error.value = 'Le serveur a renvoyé une réponse invalide. Veuillez réessayer plus tard.'
        } else {
          const serverMsg = err.response?.data?.error || err.response?.data?.message || ''
          error.value = serverMsg || err.message || 'Une erreur est survenue lors de la connexion'
          const lowered = String(serverMsg).toLowerCase()
          if (
            err.response?.data?.code === 'ACCOUNT_NOT_ACTIVATED' ||
            lowered.includes('non activ') ||
            lowered.includes('verrou')
          ) {
            notActivated.value = true
          }
        }
      } finally {
        isLoading.value = false
      }
    }

    const startResend = () => {
      resendError.value = ''
      resendMessage.value = ''
      if (!email.value) {
        resendError.value = 'Veuillez renseigner votre adresse email.'
        return
      }
      showCaptcha.value = true
    }

    const confirmResend = async () => {
      try {
        resendLoading.value = true
        resendError.value = ''
        resendMessage.value = ''
        await axiosInstance.post('/auth/resend-activation/', {
          email: email.value,
          captchaToken: captchaToken.value,
        })
        resendMessage.value = "Si un compte non activé existe pour cet email, un nouveau lien a été envoyé."
        showCaptcha.value = false
        captchaToken.value = ''
      } catch (err) {
        if (err.response?.status === 429) {
          const retry = Number(err.response.headers?.['retry-after'] || 60)
          startCooldown(retry)
          resendError.value = `Trop de demandes. Réessayez dans ${retry}s.`
        } else {
          resendError.value = err.response?.data?.error || 'Une erreur est survenue'
        }
      } finally {
        resendLoading.value = false
      }
    }

    return {
      email,
      password,
      error,
      isLoading,
      handleSubmit,
      backgroundLoaded,
      backgroundStyle,
      notice,
      notActivated,
      showCaptcha,
      captchaToken,
      resendLoading,
      resendMessage,
      resendError,
      startResend,
      confirmResend,
      cooldownSeconds,
      isLegalModalOpen,
    }
  }
}
</script>

<style>
.login-container {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: opacity 0.5s ease-in-out;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
}

.login-form-container {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}

@media (min-width: 640px) {
  .login-form-container {
    padding: 1rem;
  }
}
</style>
